import { Component, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { MatStepper } from "@angular/material/stepper";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { BehaviorSubject } from "rxjs";
import { AuthService } from "src/app/core/service/auth.service";
import { SnackbarService } from "src/app/core/service/snackbar.service";
import { SubscriptionService } from "src/app/core/service/subscription.service";
import { UserService } from "src/app/core/service/user.service";
import { CategoriaTO } from "src/app/data/schema/Courses/CategoriaTO";
import { Tag } from "src/app/data/schema/Courses/Tag";
import { SubscriptionDTO } from "src/app/data/schema/Subscriptions/SubscriptionDTO";
import { UserCustomFieldDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldDTO";
import { UserCustomFieldTypeDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldTypeDTO";
import { UserCompleteRegistrationDTO } from "src/app/data/schema/User/UserCompleteRegistrationDTO";
import { UsuarioTO } from "src/app/data/schema/User/UsuarioTO";
import { ApiCoursesService } from "src/app/data/service/courses/api-courses.service";
import { DataAnalysisService } from "src/app/data/service/data-analysis/data-analysis.service";
import { ApiUserService } from "src/app/data/service/user/api-user.service";
import { PrivacyPoliceDialogComponent } from "src/app/shared/component/dialogs/privacy-police-dialog/privacy-police-dialog.component";
import { TermsOfUseDialogComponent } from "src/app/shared/component/dialogs/terms-of-use-dialog/terms-of-use-dialog.component";
import { UserCustomFieldsComponent } from "src/app/shared/component/user-custom-fields/user-custom-fields.component";
import { environment } from "src/environments/environment";
import enUS from "./i18n/en-US.json";
import esES from "./i18n/es-ES.json";
import ptBR from "./i18n/pt-BR.json";

@Component({
    selector: "app-complete-registration",
    templateUrl: "./complete-registration.component.html",
    styleUrls: ["./complete-registration.component.scss"]
})
export class CompleteRegistrationComponent implements OnInit {
    nomeFormGroup: UntypedFormGroup;
    emailFormGroup: UntypedFormGroup;
    termosUsoFormGroup: UntypedFormGroup;
    politicaPrivacidadeFormGroup: UntypedFormGroup;

    subscription: SubscriptionDTO;
    termosUso: string;
    politicaPrivacidade: string;
    isLoading: boolean;
    error: string;
    errorApi: any;
    success: string;
    cadastrado: boolean;
    minDate: Date;
    maxDate: Date;
    uploadingFiles = false;

    tags: Tag[];

    TermsOfUseClicked = false;
    privacyPolicyClicked = false;
    showCategories = true;
    userCustomFields$: BehaviorSubject<UserCustomFieldDTO[]>;
    skipCompleteRegistration = false;

    get showUserCustomFieldsForm(): boolean {
        return this.userCustomFields$?.value.length > 0;
    }

    get allowSkipCompleteRegistration(): boolean {
        return this.subscription?.features?.allowSkipCompleteRegistration?.active;
    }

    user: UsuarioTO;
    userId: string;
    callBack: string;

    @ViewChild("appUserCustomFields") userCustomFieldsComponent: UserCustomFieldsComponent;
    @ViewChild("stepper") stepper: MatStepper;

    constructor(
        private translate: TranslateService,
        private formBuilder: UntypedFormBuilder,
        private coursesService: ApiCoursesService,
        private apiUserService: ApiUserService,
        private userService: UserService,
        private authService: AuthService,
        private router: Router,
        private subscriptionService: SubscriptionService,
        public matDialog: MatDialog,
        private dataAnalysisService: DataAnalysisService,
        private route: ActivatedRoute,
        private snackBar: SnackbarService,
        private spinner: NgxSpinnerService
    ) {
        this.translate.setTranslation("pt-BR", ptBR, true);
        this.translate.setTranslation("en-US", enUS, true);
        this.translate.setTranslation("es-ES", esES, true);

        this.matDialog.closeAll();
        this.tags = new Array<Tag>();
        this.isLoading = false;
        this.error = null;
        this.success = null;
        this.cadastrado = false;
        this.userCustomFields$ = new BehaviorSubject<UserCustomFieldDTO[]>([]);
        this.minDate = new Date(1900, 1, 1);
        this.maxDate = new Date(Date.now());
    }

    ngOnInit(): void {
        if (this.authService.getCurrentSubscriptionId() == null)
            this.subscriptionService.setCurrentSubscriptionId(environment.default_subscription_id);

        this.termosUso = this.subscriptionService.currentSubscription?.settings?.termsOfUseUrl;

        this.politicaPrivacidade = this.subscriptionService.currentSubscription?.settings?.privacyPolicyUrl;
        this.showCategories = this.subscriptionService.currentSubscription?.features?.coursesMatch?.active;
        this.coursesService.getCategorias().subscribe((data: CategoriaTO[]) => {
            data.forEach((element) => {
                this.tags.push(new Tag(element));
            });
        });

        this.userCustomFields$.next(this.subscriptionService.currentSubscription?.settings?.userCustomFields);

        this.nomeFormGroup = this.formBuilder.group({
            nome: ["", Validators.required],
            sobrenome: ["", Validators.required]
        });

        this.emailFormGroup = this.formBuilder.group({
            email: ["", [this.customRequired, Validators.email]]
        });

        this.termosUsoFormGroup = this.formBuilder.group({
            aceitoTermos: ["", Validators.required]
        });

        this.politicaPrivacidadeFormGroup = this.formBuilder.group({
            concordoPoliticaPrivacidade: ["", Validators.required]
        });

        this.route.params.subscribe((data) => {
            this.userId = String(data.id);
            this.spinner.show();
            this.getUser();
        });

        this.route.queryParams.subscribe((data) => {
            this.callBack = data.callbackUrl;
        });

        this.subscriptionService.currentSubscriptionAsync().subscribe((currentSub) => (this.subscription = currentSub));
    }

    customRequired: ValidatorFn = (control: UntypedFormGroup): ValidationErrors | null => {
        if (this.userCustomFields$?.value?.length) {
            return null;
        }

        return !control.value && control.dirty ? { required: true } : null;
    };

    getUser(): void {
        this.apiUserService.getUserById(this.userId).subscribe(
            (user) => {
                this.user = user;
                this.setUser();
                this.spinner.hide();
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
            }
        );
    }

    setUser(): void {
        this.nomeFormGroup.controls.nome.setValue(this.user.nome);
        this.nomeFormGroup.controls.sobrenome.setValue(this.user.sobrenome);
        this.emailFormGroup.controls.email.setValue(this.user.email);

        if (this.user.email) this.emailFormGroup.controls.email.disable();

        this.selectTags();
    }

    selectTags = (): void => {
        if (!this.user || this.tags?.length <= 0) return;

        this.tags.forEach((tag) => {
            tag.selected = this.user.tagsInteressesPrincipais.includes(tag.nome);

            tag.subCategorias.forEach((sub) => {
                sub.selected = this.user.tagsInteressesDetalhes.includes(sub.nome);
            });
        });
    };

    selectMainTag(id: string) {
        const selectedTag = this.tags.filter((tag) => tag.id === id)[0];
        selectedTag.selected = !selectedTag.selected;

        for (let index = 0; index < selectedTag.subCategorias.length; index++) {
            const child = selectedTag.subCategorias[index];
            child.selected = false;
        }
    }

    selectChildrenTag(idParent: string, idChild: string) {
        const selectedParentTag = this.tags.filter((tag) => tag.id === idParent)[0];

        const selectedTag = selectedParentTag.subCategorias.filter((child) => child.id === idChild)[0];
        selectedTag.selected = !selectedTag.selected;
    }

    getSelectedTags() {
        return this.tags.filter((tag) => tag.selected);
    }

    onChange($event: MatSlideToggleChange, stepper: MatStepper) {
        if ($event.checked) {
            stepper.next();
        }
    }

    save() {
        this.isLoading = true;
        this.error = null;
        this.errorApi = null;
        this.success = null;

        const tagsInteressesPrincipais = new Array<string>();

        this.tags
            .filter((tag) => tag.selected)
            .forEach((element) => {
                tagsInteressesPrincipais.push(element.nome);
            });

        const tagsInteressesDetalhes = new Array<string>();

        this.tags
            .filter((tag) => tag.selected)
            .forEach((element) => {
                element.subCategorias
                    .filter((child) => child.selected)
                    .forEach((e) => {
                        tagsInteressesDetalhes.push(e.nome);
                    });
            });

        const filefieldName = this.userCustomFields$.value
            .filter((x) => x.type == UserCustomFieldTypeDTO.File)
            ?.map((x) => x.fieldName);

        const complete: UserCompleteRegistrationDTO = {
            id: this.user.id,
            nome: this.nomeFormGroup.value.nome,
            sobrenome: this.nomeFormGroup.value.sobrenome,
            email: this.emailFormGroup.value.email,
            tagsInteressesPrincipais: tagsInteressesPrincipais,
            tagsInteressesDetalhes: tagsInteressesDetalhes,
            customFields: this.userCustomFieldsComponent?.exportValues() || null,
            dataNotReviewed: this.skipCompleteRegistration
        };

        this.apiUserService.saveCompleteRegistration(complete).subscribe(
            (user) => {
                this.userService.setCurrentUser(user);
                this.dataAnalysisService.CompleteRegistration(
                    "APP",
                    this.nomeFormGroup.value.nome + " " + this.nomeFormGroup.value.sobrenome,
                    this.emailFormGroup.value.email,
                    true
                );

                filefieldName?.forEach((field) => {
                    const file = this.userCustomFieldsComponent?.filesToUpload[field];
                    const last = filefieldName.indexOf(field) == filefieldName.length - 1;
                    if (file) {
                        this.uploadingFiles = true;
                        this.uploadFileCustomField(this.user.id, file, field, last);
                    }
                });

                this.cadastrado = true;
                this.translate.get("REGISTER.DONE.MESSAGES.CREATED").subscribe((res) => {
                    this.snackBar.showMessage(res, 3000);
                });
                this.isLoading = false;

                if (this.callBack) this.router.navigateByUrl(this.callBack);
                else {
                    if (!this.subscription.features.redirectMyCoursesLogin.active) this.router.navigate(["home"]);
                    else this.router.navigate(["home", "cursos"]);
                }
            },
            (error) => {
                if (error?.error?.includes("email already in use") != null) {
                    this.translate.get("REGISTER.USERNAME_ALREADY_IN_USER").subscribe((msg) => {
                        this.snackBar.showMessage(msg, 5000);
                    });
                } else {
                    this.translate
                        .get("REGISTER.DONE.MESSAGES.CREATE_GENERIC_ERROR")
                        .subscribe((res) => (this.error = res));
                }

                if (error.status >= 400 && error.status <= 499) this.errorApi = error.error.errors;
                this.isLoading = false;
            }
        );
    }

    setSkipCompleteRegistration() {
        this.skipCompleteRegistration = true;
        localStorage.setItem("skipCompleteRegistration", "true");
        
        // Marca o step atual como completo para permitir navegação
        if (this.stepper.selected) {
            this.stepper.selected.completed = true;
        }
        
        this.stepper.next();
    }

    openTermsOfUseDialog(): void {
        this.TermsOfUseClicked = true;
        this.matDialog.open(TermsOfUseDialogComponent);
    }

    openPrivacyPoliceDialog(): void {
        this.privacyPolicyClicked = true;
        this.matDialog.open(PrivacyPoliceDialogComponent);
    }

    trimEmail(): void {
        this.emailFormGroup.controls.email.setValue(this.emailFormGroup.controls.email.value.trim());
    }

    public uploadFileCustomField(userId: string, input: HTMLInputElement, fieldKey: string, last: boolean): void {
        if (input.files.length > 0)
            this.apiUserService.uploadFileCustomField(userId, input.files[0], fieldKey).subscribe(
                () => {
                    if (last) this.uploadingFiles = false;
                },
                () => {
                    if (last) this.uploadingFiles = false;
                }
            );
    }
}
